import {
  Box,
  Button,
  Grid2,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
  useTheme,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { ButtonStyles, StepperStyles } from "../../styles/commonStyles";
import { useIsMobile } from "../../hook/useIsMobile";
import { useDispatch, useSelector } from "react-redux";
import GenericInfoForm from "../forms/genericInfoForm/genericInfoForm.component";
import CropHistoryForm from "../forms/cropHistory/cropHistoryForm.component";
import FieldMapForm from "../forms/fieldMapForm/fieldMapForm.component";
import {
  resetFormData,
  updateFormData,
} from "../../store/features/customer/fieldForm/fieldFormSlice";
import SubAreaForm from "../forms/subAreaForm/subAreaForm.component";
import {
  createFieldStart,
  resetCreateFieldState,
} from "../../store/features/customer/field/fieldSlice";
import SuccessDialog from "../muiDialogs/successDialog.component";
import { dialogMessages } from "../forms/utils";
import { useNavigate } from "react-router";

const allSteps = [
  {
    label: "Info generali",
    formId: "generic-info-form",
    id: "genericInfo",
  },
  {
    label: "Storico colture",
    formId: "crop-history-row-form",
    id: "cropHistory",
  },
  {
    label: "Mappa",
    formId: "map-form",
    id: "fieldMap",
  },
  {
    label: "Sottoterreni",
    formId: "sub-areas-form",
    id: "subAreas",
  },
];

const buttons = (handleDialogConfirm, handleDialogNavigate) => [
  {
    variant: "outlined",
    label: "Vai a I miei terreni",
    action: handleDialogConfirm,
  },
  {
    variant: "contained",
    label: "Visualizza terreno",
    action: handleDialogNavigate,
  },
];

const MuiStepper = ({ crops, irrigationTypes, fieldsType }) => {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { success } = useSelector((state) => state.field);
  const fieldId = useSelector((state) => state.field?.data?.data?.id);
  const fieldForm = useSelector((state) => state.fieldForm?.formData);
  const accessToken = useSelector((state) => state?.auth?.user?.access_token);

  const [activeStep, setActiveStep] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({});
  const [hasSubAreas, setHasSubAreas] = useState(true);

  const steps = useMemo(() => {
    if (hasSubAreas) {
      return allSteps;
    } else {
      return allSteps.filter((step) => step.id !== "subAreas");
    }
  }, [hasSubAreas]);

  const handleHasSubAreasChange = (value) => {
    setHasSubAreas(value);
  };

  const handleNext = (data) => {
    const currentStep = steps[activeStep];
    const updatedFormData = {
      ...formData,
      [currentStep.label]: data,
    };
    setFormData(updatedFormData);
    const key = currentStep.id;
    dispatch(updateFormData({ key, data }));

    if (activeStep === steps.length - 1) {
      const finalFormData = {
        ...fieldForm,
        [key]: data,
      };
      const fieldColtureArray = [];

      if (finalFormData?.genericInfo?.field_colture) {
        fieldColtureArray.push({
          colture: finalFormData?.genericInfo?.field_colture,
          start_date: finalFormData?.genericInfo?.start_date,
          end_date: finalFormData?.genericInfo?.end_date,
        });
      }

      if (
        finalFormData?.cropHistory &&
        Array.isArray(finalFormData?.cropHistory)
      ) {
        fieldColtureArray.push(
          ...finalFormData?.cropHistory?.map((crop) => ({
            colture: crop?.colture,
            start_date: crop?.start_date,
            end_date: crop?.end_date,
          }))
        );
      }
      const formattedFormData = {
        name: finalFormData?.genericInfo?.name,
        field_type: finalFormData?.genericInfo?.field_type,
        irrigation_system: finalFormData?.genericInfo?.irrigation_system,
        organic_soil_percentage:
          finalFormData?.genericInfo?.organicSubstance || 0,
        stone_percentage: finalFormData?.genericInfo?.stoneDensity || 0,
        polygon: finalFormData?.fieldMap?.polygonCoordinates,
        has_sub_field: finalFormData?.genericInfo?.has_sub_field,

        field_colture: fieldColtureArray,

        field_parts: finalFormData?.subAreas?.map((subArea) => ({
          name: subArea?.subAreaName,
          field_type: subArea?.subAreaType,
          organic_soil_percentage: subArea?.organicSubstance,
          stone_percentage: subArea?.stoneDensity,
          polygon: subArea?.subPolygonCoordinates,
        })),
      };
      dispatch(
        createFieldStart({ data: formattedFormData, accessToken: accessToken })
      );
    } else {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => setActiveStep((prev) => prev - 1);

  const handleDialogConfirm = () => {
    dispatch(resetCreateFieldState());
    dispatch(resetFormData());
    navigate("/userLands");
  };

  const handleDialogNavigate = () => {
    dispatch(resetCreateFieldState());
    dispatch(resetFormData());
    navigate(`/userLands/${fieldId}`);
  };

  useEffect(() => {
    if (success && !dialogOpen) {
      setDialogOpen(true);
    }
  }, [dialogOpen, success]);

  return (
    <Box width="100%" height="100%">
      <Grid2 container spacing={4} height="100%">
        {!isMobile && (
          <Grid2 size={{ xs: 12, md: 3 }} height="100%">
            <Box
              width="100%"
              bgcolor={theme.palette.custom.backgroundColor}
              p={2}
              display="flex"
              justifyContent="center"
              height="100%"
            >
              <Stepper
                activeStep={activeStep}
                orientation="vertical"
                sx={StepperStyles}
              >
                {steps.map((step) => (
                  <Step key={step.label}>
                    <StepLabel>{step.label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Box>
          </Grid2>
        )}
        <Grid2 size={{ xs: 12, md: 9 }} height="100%">
          <Box width="100%" height="100%">
            <Stack height="100%">
              {activeStep === 0 && (
                <GenericInfoForm
                  fieldsType={fieldsType}
                  irrigationTypes={irrigationTypes}
                  crops={crops}
                  onSubmit={(data) => handleNext(data)}
                  theme={theme}
                  onHasSubAreasChange={handleHasSubAreasChange}
                  initialData={fieldForm?.genericInfo || null}
                />
              )}
              {activeStep === 1 && (
                <CropHistoryForm
                  theme={theme}
                  crops={crops}
                  onSubmit={(data) => handleNext(data)}
                  formData={formData}
                  fieldForm={fieldForm?.CropHistory || null}
                />
              )}
              {activeStep === 2 && (
                <>
                  <Typography fontWeight="bold" fontSize="16px" mb={2}>
                    Mappa
                  </Typography>
                  <FieldMapForm
                    formData={formData}
                    onSubmit={(data) => handleNext(data)}
                    savedData={fieldForm?.fieldMap || null}
                    onDataChange={(data) => {
                      setFormData((prev) => ({
                        ...prev,
                        fieldMap: {
                          ...prev.fieldMap,
                          ...data,
                        },
                      }));
                    }}
                  />
                </>
              )}
              {activeStep === 3 && (
                <SubAreaForm
                  formData={formData.subAreas}
                  fieldPosition={
                    formData["Mappa"]?.polygonCoordinates ||
                    formData.Mappa.polygonCoordinates
                  }
                  fieldsType={fieldsType}
                  onSubmit={(data) => handleNext(data)}
                  onDataChange={(data) => {
                    setFormData((prev) => ({
                      ...prev,
                      subAreas: data,
                    }));
                  }}
                />
              )}
              <Box
                width="100%"
                height="auto"
                display="flex"
                alignItems="center"
                justifyContent={activeStep > 0 ? "space-between" : "end"}
                gap={2}
              >
                {activeStep > 0 && (
                  <Button
                    variant="outlined"
                    onClick={handleBack}
                    sx={ButtonStyles}
                  >
                    Indietro
                  </Button>
                )}
                <Button
                  variant="contained"
                  form={steps[activeStep]?.formId}
                  type="submit"
                  sx={ButtonStyles}
                >
                  {activeStep === steps.length - 1 ? "Salva" : "Continua"}
                </Button>
              </Box>
            </Stack>
          </Box>
        </Grid2>
      </Grid2>
      <SuccessDialog
        open={dialogOpen}
        onConfirm={handleDialogConfirm}
        title={dialogMessages.addCustomerField.title}
        description={dialogMessages.addCustomerField.description}
        buttons={buttons(handleDialogConfirm, handleDialogNavigate)}
      />
    </Box>
  );
};

export default MuiStepper;
